<template>
  <y-combobox
    v-if="isGBCompanySearch"
    ref="searchRef"
    v-model="selectedOption"
    v-model:search="searchTerm"
    :loading="isLoading"
    :items="displayOptions"
    :label="t('components.company_search.company_name_label')"
    item-title="companyName"
    :custom-filter="filter"
    :error-messages="props.errorMessages"
    @update:search="searchCompanyDebounced"
  >
    <template #item="{ props: childProps, item }">
      <v-list-item v-if="item.raw.rawInput && searchTerm" @click="() => onClick(childProps)">
        <v-card-item>
          <v-card-subtitle>
            <span>{{ t("components.company_search.not_found_prompt") }}</span>
            &nbsp;
            <v-chip color="purple-6">
              {{ searchTerm }}
            </v-chip>
          </v-card-subtitle>
        </v-card-item>
      </v-list-item>
      <v-list-item v-else @click="() => onClick(childProps)">
        <v-card-item>
          <v-card-title class="text-body-1 font-weight-regular">
            {{ item.raw.companyName }}
          </v-card-title>
          <v-card-subtitle>
            {{ item.raw.addressLine1 }}, {{ item.raw.postalCode }}, {{ item.raw.city }}
          </v-card-subtitle>
        </v-card-item>
      </v-list-item>
    </template>
  </y-combobox>

  <FormTextInput
    v-else
    ref="inputRef"
    v-model="legalEntityTyped"
    autocapitalize="words"
    :error-messages="props.errorMessages"
    @update:model-value="onLegalEntityTyped"
  />
</template>

<script setup lang="ts">
import { Items } from "@companieshouse/api-sdk-node/dist/services/search/advanced-search/types";
import { AxiosError } from "axios";
import { ref, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useCompanyHouseApi } from "@/composables/useCompanyHouseApi";
import { Company, toCompany } from "@/utils/company";

const props = defineProps({
  errorMessages: {
    type: Array<string>,
    required: false,
    default: () => [],
  },
  country: {
    type: String,
    required: false,
    default: "GB",
  },
  isCompany: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const searchRef = ref<HTMLInputElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

const emit = defineEmits<{ result: Array<Partial<Company>> }>();
const { api: companyHouseApi } = useCompanyHouseApi();
const { t } = useI18n();

interface DisplayOption extends Company {
  rawInput?: boolean;
}

const isLoading = ref<boolean>(false);
const companies = ref<Array<Company>>();
const searchTerm = ref<string | undefined>();
const selectedOption = ref<DisplayOption | string | undefined>();
const legalEntityTyped = ref<string>("");
let timerId: number;
const MIN_SEARCH_TERM_LENGTH = 3;

const isGBCompanySearch = computed(() => props.country.toLowerCase() === "gb" && props.isCompany);

const focus = () => {
  const focusField = isGBCompanySearch.value ? searchRef : inputRef;
  focusField.value?.focus({ preventScroll: true });
};

watch(selectedOption, (selected) => {
  // if we have a string, it means the user typed something and didn't match any result from the api
  const selectedName = typeof selected === "string" ? selected : selected?.companyName;
  const company = companies.value?.find((c) => c.companyName === selectedName);
  //if we just emit the company_name, then we will collect the rest of the data later
  emit("result", company || { companyName: selectedName });
});

const displayOptions = computed<Array<DisplayOption>>(() => {
  const items = companies.value?.map<DisplayOption>((item) => ({ ...item })) ?? [];
  if (searchTerm.value) {
    items.push({ rawInput: true, companyName: searchTerm.value });
  }
  return items;
});

const onLegalEntityTyped = (searchTerm: string) => {
  emit("result", { companyName: searchTerm });
};

const searchCompany = async (searchTerm: string) => {
  //if search too short we don't bother hitting the api
  //we just show the typed option
  if (searchTerm.length < MIN_SEARCH_TERM_LENGTH) {
    companies.value = [];
    return;
  }

  //check if we have an exact match already. If we do, we don't need to hit the api
  const company = companies.value?.find((c) => c.companyName === searchTerm);
  if (company) {
    return;
  }

  isLoading.value = true;
  try {
    const res = await companyHouseApi?.advancedSearchCompanies(searchTerm);
    companies.value = res.items.map((item: Items) => toCompany(item, props.country)) || [];
  } catch (e: unknown) {
    if (e instanceof AxiosError && e?.response?.status === 404) {
      companies.value = [];
      return;
    }
    console.warn("error company search", e);
  } finally {
    isLoading.value = false;
  }
};

const searchCompanyDebounced = (searchTerm: string) => {
  //if we have a timer running we clear it
  clearTimeout(timerId);
  //let's wait some time before searching,
  //so we don't hit the api on every key stroke
  timerId = window.setTimeout(() => searchCompany(searchTerm), 500);
};

//no need to filter, we want to show all results from the api
const filter = () => true;

const onClick = (props: Record<string, unknown>) => {
  if (props.onClick && typeof props.onClick === "function") {
    props.onClick();
  }
};

defineExpose<{ focus: () => void }>({
  focus,
});
</script>
